import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewContainerRef
} from "@angular/core";
import { NgClass, NgForOf, NgIf } from "@angular/common";
import { Rule, TariffRuleSet } from "../../../../models/tariff-rule-set";
import { SvgIconComponent } from "../../../svg-icon/svg-icon.component";
import { FormsModule } from "@angular/forms";
import { animate, keyframes, style, transition, trigger } from "@angular/animations";
import { TariffTableRulesComponent } from "./tariff-table-rules/tariff-table-rules.component";
import { RightPanelComponent } from "../../../right-panel/right-panel.component";
import { FinanceApiService } from "../../../../services/finance-a-p-iervice.service";
import { ActivatedRoute } from "@angular/router";
import { TooltipDirective } from "../../../../directives/tooltip.directive";
import { DeterminantDetails } from "../../../../models/determinant-details";
import { ModalComponent } from "../../../modal/modal.component";
import { ConfirmDialogComponent } from "../../../confirm-dialog/confirm-dialog.component";
import { ToasterService } from "../../../../services/toaster.service";
import { TariffTableConfigs } from "../../config/TariffTableConfigs";
import { TARIFF_ACTIONS, TariffService } from "../../../../services/tariff.service";

@Component({
  selector: "app-tariff-table",
  standalone: true,
  imports: [
    NgForOf,
    NgIf,
    SvgIconComponent,
    FormsModule,
    TooltipDirective,
    NgClass
  ],
  templateUrl: "tariff-table.html",
  styleUrl: "tariff-table.scss",
  animations: [
    trigger("tabAnimation", [
      transition(":enter", [
        style({ opacity: 0, transform: "scale(0.8) translateY(-10px)" }),
        animate("200ms ease-out", style({ opacity: 1, transform: "scale(1) translateY(0)" }))
      ]),
      transition(":leave", [
        animate("150ms ease-in", style({ opacity: 0, transform: "scale(0.8) translateY(-10px)" }))
      ])
    ]),
    trigger("blinkAnimation", [
      transition(":enter", [
        animate("1s", keyframes([
          style({ backgroundColor: "#F5FBFF", offset: 0 }),
          style({ backgroundColor: "#ffffff", offset: 1 })
        ]))
      ])
    ])
  ]
})
export class TariffTable {
  addButtonVisible: boolean = false;
  readonly ruleSetsInit: TariffRuleSet = {
    "description": "Rule Set",
    "rows": [
      {
        "rules": []
      }
    ],
    "cols": [
      {
        "rules": []
      }
    ],
    "values": [[
      {
        "minTariffExpression": "0",
        "tariffExpression": "0"
      }
    ]]
  };
  selectedRuleSetIndex: number = 0;
  descriptionEditable: boolean = false;
  validationErrors: { [key: string]: boolean } = {};
  highlightHeaders: { colIndex: number; rowIndex: number } = { colIndex: -1, rowIndex: -1 };

  @Input() ruleSets: TariffRuleSet[] = [];
  @Input() determinants: DeterminantDetails[] = [];

  @ViewChild("descriptionTextArea") descriptionTextArea!: ElementRef;
  @ViewChildren("ColumnHeaderLetter", { read: ElementRef }) columnHeaderLetters!: QueryList<ElementRef>;

  @Output() ruleSetsChange = new EventEmitter<any>();

  constructor(private viewContainerRef: ViewContainerRef,
              private financeService: FinanceApiService,
              private route: ActivatedRoute,
              private toasterService: ToasterService,
              private tariffService: TariffService) {
  }

  ngOnInit() {
    const path = this.route.snapshot.url[0].path;
    if (path === TARIFF_ACTIONS.ADD_NEW || this.tariffService.currentState.appMode === TARIFF_ACTIONS.ADD_NEW)
      this.addNewRuleSet();
  }

  addColumns(ruleSet: TariffRuleSet, event: Event) {
    if (ruleSet.cols.length >= TariffTableConfigs.maxColumns) {
      return;
    }

    // Validate all fields before adding a new column
    if (!this.validateAllFields(ruleSet)) {
      return;
    }

    if (ruleSet.cols[ruleSet.cols.length - 1]?.rules?.length === 0) {
      this.toasterService.error("Add rules to the existing column before proceeding.");
      return;
    }

    // add a new column with empty rules
    ruleSet.cols.push({ rules: [] });

    // add a new tariff cell to each existing row
    ruleSet.values.forEach((row: any[]) => {
      row.push({
        minTariffExpression: "0",
        tariffExpression: "0"
      });
    });

    //scroll to the end of columns when new columns are added
    this.scrollToRight(event);
  }

  deleteColumns(ruleSet: TariffRuleSet, colIndex: number) {
    if (colIndex < -1) return;

    const componentRef = this.showConfirmationModal("Column", colIndex);

    componentRef.confirmDialogComponentComponentRef.instance.confirm.subscribe(() => {
      ruleSet.cols.splice(colIndex, 1);
      ruleSet.values.forEach(tariff => tariff.splice(colIndex, 1));
      componentRef.modalComponentRef.destroy();
    });
  }

  addRows(ruleSet: TariffRuleSet, event: Event) {
    if (ruleSet.rows.length >= TariffTableConfigs.maxRows) {
      return;
    }

    // Validate all fields before adding a new row
    if (!this.validateAllFields(ruleSet)) {
      return;
    }

    if (ruleSet.rows[ruleSet.rows.length - 1]?.rules?.length === 0) {
      this.toasterService.error("Add rules to the existing row before proceeding.");
      return;
    }

    // add a new row with empty rules
    ruleSet.rows.push({ rules: [] });

    // create a new tariff row with cells equal to the number of columns
    const newRow = new Array(ruleSet.cols.length)
      .fill({})
      .map(() => ({
        minTariffExpression: "0",
        tariffExpression: ""
      }));
    ruleSet.values.push(newRow);

    this.scrollToBottom(event);
  }

  deleteRows(ruleSet: any, rowIndex: number, event: Event) {
    if (rowIndex < -1) return;

    const componentRef = this.showConfirmationModal("Row", rowIndex);

    componentRef.confirmDialogComponentComponentRef.instance.confirm.subscribe(() => {
      ruleSet.rows.splice(rowIndex, 1);
      ruleSet.values.splice(rowIndex, 1);
      componentRef.modalComponentRef.destroy();
    });
  }

  addConditionsForSelectedRowOrColumn(ruleSetIndex: number, headerIndex: number, headerType: string) {
    const panelComponentComponentRef = this.setUpRightPanel();

    const determinantRulesComponentRef = panelComponentComponentRef.instance.loadComponent(TariffTableRulesComponent);
    determinantRulesComponentRef.setInput("allDeterminants", this.determinants);
    determinantRulesComponentRef.setInput("ruleSets", this.ruleSets);

    determinantRulesComponentRef.instance.patchRulesValue(ruleSetIndex, headerIndex, headerType);

    //check for apply all button of the right panel and then call the applyRules method of determinantRulesComponent
    panelComponentComponentRef.instance.primaryActionBtnEmitter.subscribe(() => {
      determinantRulesComponentRef.instance.applyRules(ruleSetIndex, headerIndex, headerType);
      if (determinantRulesComponentRef.instance.filterForm.valid) panelComponentComponentRef.destroy();
    });

    determinantRulesComponentRef.instance.applyAllButtonClicked.subscribe((ruleSets: any) => {
      this.ruleSets = ruleSets;
      this.ruleSetsChange.emit(this.ruleSets);
    });
  }

  private filterDeterminants(headerType: string, ruleSetIndex: number) {
    return this.determinants.filter(determinant => {
      if (headerType === "row") {
        return this.ruleSets[ruleSetIndex]?.cols.some(col => col.rules.some(rule => rule.determinant !== determinant.name));
      } else {
        return this.ruleSets[ruleSetIndex]?.rows.some(row => row.rules.some(rule => rule.determinant !== determinant.name));
      }
    });
  }

  private setUpRightPanel() {
    const modalRef = this.viewContainerRef.createComponent(RightPanelComponent);
    modalRef.instance.title = "Add Rule";
    modalRef.instance.showActionBtns = true;
    modalRef.instance.primaryActionBtnText = "Apply";
    modalRef.instance.close.subscribe(() => {
      modalRef.destroy();
    });
    return modalRef;
  }

  private scrollToBottom(event: Event) {
    setTimeout(() => {
      const button = event.target as HTMLElement;
      const rulesMatrix = button.closest(".rules-matrix");
      if (rulesMatrix) {
        const tableContainer = rulesMatrix.querySelector(".table-container") as HTMLElement;
        if (tableContainer) {
          tableContainer.scrollTop = tableContainer.scrollHeight;
        }
      }
    }, 10);
  }

  private scrollToRight(event: Event) {
    setTimeout(() => {
      const button = event.target as HTMLElement;
      const rulesMatrix = button.closest(".rules-matrix");
      if (rulesMatrix) {
        const tableContainer = rulesMatrix.querySelector(".table-container") as HTMLElement;
        if (tableContainer) {
          tableContainer.scrollLeft = tableContainer.scrollWidth;
        }
      }
    }, 10);
  }

  getValuesToPrint(rule: Rule) {
    return this.tariffService.getValuesToPrint(rule);
  }

  addNewRuleSet() {
    if (this.ruleSets.length >= TariffTableConfigs.maxRules) return;

    // Validate all fields across all rule sets
    let hasEmptyFields = false;
    this.ruleSets.forEach((ruleSet, ruleSetIndex) => {
      ruleSet.values.forEach((row, rowIndex) => {
        row.forEach((cell, colIndex) => {
          if (!cell.minTariffExpression) {
            this.validateField(ruleSetIndex, rowIndex, colIndex, "minTariffExpression");
            hasEmptyFields = true;
          }
          if (!cell.tariffExpression) {
            this.validateField(ruleSetIndex, rowIndex, colIndex, "tariffExpression");
            hasEmptyFields = true;
          }
        });
      });
    });

    if (hasEmptyFields) {
      this.toasterService.error("Please fill all tariff fields in existing rule sets before adding a new one.");
      return;
    }

    this.ruleSetsInit.description = `Rule Set ${this.ruleSets.length + 1}`;
    this.ruleSets.push(JSON.parse(JSON.stringify(this.ruleSetsInit)));
    this.selectedRuleSetIndex = this.ruleSets.length - 1;
  }

  deleteRuleSet(index: number, event: MouseEvent) {
    const showConfirmationModal = this.showConfirmationModal("Rule Set", index);
    showConfirmationModal.confirmDialogComponentComponentRef.instance.confirm.subscribe(() => {
      event.stopPropagation();
      this.ruleSets.splice(index, 1);

      if (this.selectedRuleSetIndex >= index) {
        this.selectedRuleSetIndex = Math.max(0, this.selectedRuleSetIndex - 1);
      }
      showConfirmationModal.modalComponentRef.destroy();
    });
  }

  getDisplayTextForDeterminant(determinantName: string | undefined) {
    return this.tariffService.getDisplayTextForDeterminant(determinantName, this.determinants);
  }

  getDisplayTextForOperator(operatorValue: string | undefined): string {
    return this.tariffService.getDisplayTextForOperator(operatorValue);
  }

  showDescriptionTextArea() {
    this.descriptionEditable = true;
    setTimeout(() => {
      this.descriptionTextArea.nativeElement.focus();
    }, 1);
  }

  hideDescriptionArea(ruleSet: TariffRuleSet) {
    const descriptionValueEntered = this.descriptionTextArea.nativeElement.value;

    if (descriptionValueEntered !== "")
      ruleSet.description = descriptionValueEntered;
    this.descriptionEditable = false;
  }

  showAddRowColBtns() {
    this.addButtonVisible = true;
  }

  hideAddRowColBtns() {
    this.addButtonVisible = false;
  }

  getColumnLetter(index: number): string {
    let letter = "";
    while (index >= 0) {
      letter = String.fromCharCode((index % 26) + 65) + letter;
      index = Math.floor(index / 26) - 1;
    }
    return letter;
  }

  highLightRowAndCol(colIndex: number, rowIndex: number) {
    this.highlightHeaders = { colIndex, rowIndex };
  }

  selectAllCols(colIndex: number) {
    const columns = document.querySelectorAll(`.matrix-table tbody tr td:nth-child(${colIndex + 2})`);
    columns.forEach(column => {
      column.classList.add("highlighted");
    });
  }

  unselectAllCols = (colIndex: number) => {
    const columns = document.querySelectorAll(`.matrix-table tbody tr td:nth-child(${colIndex + 2})`);
    columns.forEach(column => {
      column.classList.remove("highlighted");
    });
  };

  selectAllRows(rowIndex: number) {
    const rows = document.querySelectorAll(`.matrix-table tbody tr:nth-child(${rowIndex + 1}) td`);

    rows.forEach(row => {
      row.classList.add("highlighted");
    });
  }

  unselectAllRows(rowIndex: number) {
    const rows = document.querySelectorAll(`.matrix-table tbody tr:nth-child(${rowIndex + 1}) td`);

    rows.forEach(row => {
      row.classList.remove("highlighted");
    });
  }

  validateField(ruleSetIndex: number, rowIndex: number, colIndex: number, field: "minTariffExpression" | "tariffExpression"): void {
    const value = this.ruleSets[ruleSetIndex].values[rowIndex][colIndex][field];
    const key = `${ruleSetIndex}-${rowIndex}-${colIndex}-${field}`;
    this.validationErrors[key] = !value;
  }

  isFieldInvalid(ruleSetIndex: number, rowIndex: number, colIndex: number, field: "minTariffExpression" | "tariffExpression"): boolean {
    const key = `${ruleSetIndex}-${rowIndex}-${colIndex}-${field}`;
    return this.validationErrors[key] || false;
  }

  private showConfirmationModal(headerType: string, index: number, type: "Delete" | "Clone" = "Delete") {
    const modalComponentRef = this.viewContainerRef.createComponent(ModalComponent);
    const confirmDialogComponentComponentRef = modalComponentRef.instance.loadComponent(ConfirmDialogComponent);

    modalComponentRef.instance.title = `Delete ${headerType}`;

    const letter = (headerType.toLowerCase() === "column") ? this.getColumnLetter(index) : index + 1;
    confirmDialogComponentComponentRef.instance.message = `Are you sure you want to ${type} the ${headerType} "${letter}"?`;

    confirmDialogComponentComponentRef.instance.cancel.subscribe(() => {
      modalComponentRef.destroy();
    });
    modalComponentRef.instance.close.subscribe(() => {
      modalComponentRef.destroy();
    });

    return { modalComponentRef, confirmDialogComponentComponentRef };
  }

  private validateAllFields(ruleSet: TariffRuleSet): boolean {
    let hasEmptyFields = false;

    // Check all cells in the rule set
    ruleSet.values.forEach((row, rowIndex) => {
      row.forEach((cell, colIndex) => {
        if (!cell.minTariffExpression) {
          this.validateField(this.ruleSets.indexOf(ruleSet), rowIndex, colIndex, "minTariffExpression");
          hasEmptyFields = true;
        }
        if (!cell.tariffExpression) {
          this.validateField(this.ruleSets.indexOf(ruleSet), rowIndex, colIndex, "tariffExpression");
          hasEmptyFields = true;
        }
      });
    });

    return !hasEmptyFields;
  }

  cloneRuleSet(index: number, event: Event) {
    const showConfirmationModal = this.showConfirmationModal("Rule Set", index,"Clone");
    showConfirmationModal.confirmDialogComponentComponentRef.instance.confirm.subscribe(() => {
      event.stopPropagation();
      const cloningRuleSet = JSON.parse(JSON.stringify(this.ruleSets[index]));
      cloningRuleSet.description = this.ruleSets[index].description + " - Clone";
      this.ruleSets.push(cloningRuleSet);

      if (this.selectedRuleSetIndex >= index) {
        this.selectedRuleSetIndex = Math.max(0, this.selectedRuleSetIndex - 1);
      }
      showConfirmationModal.modalComponentRef.destroy();
    });
  }
}
